import {
  acceptPublicGroupJoinRequest,
  archiveChatById,
  createGroupChat,
  createP2PChat,
  deleteGroupChatById,
  deleteP2PChatById,
  denyPublicGroupJoinRequest,
  getEnrichedChats,
  getGroupChatById,
  getMutedChats,
  getPublicGroupChats,
  muteChatById,
  patchGroupChat,
  pinChatById,
  sendPublicGroupJoinRequest,
  unmuteChatById,
} from "@/server/services/chatService";
import {
  addChatToLocalDB,
  addChatsToLocalDB,
  getChatByNameFromLocalDB,
  getChatFromLocalDB,
  removeChatFromLocalDB,
  updateChatInLocalDB,
} from "@/indexedDB/services/chats";
import {
  ChatType,
  CreateGroupChatPayload,
  GroupChatResponseType,
  SearchableGroupChatResponse,
} from "@/server/types/chat";
import {
  ChatEntity,
  InitChatStatus,
  MessageEntity,
  MessageSendingStatus,
} from "@/indexedDB/types";
import { MemberRole } from "@/server/types/member";
import { extractTimestampFromUUID7, generateUuid7 } from "@/utils/uuid7";

import { decryptMessageContent } from "@/middleware/crypto";
import { tryDecrypt } from "@/middleware/decryptionError";
import { isUserAuthenticated } from "./auth";

import { useNavigationState } from "@/zustand/NavigationState";
import {
  decryptPrivateKey,
  encapsulateSecurePayload,
  generateChatSecretKey,
} from "@/lib/crypto/crypto";

import {
  addMessagesToLocalDB,
  countMessagesAtEpochInLocalDB,
  removeMessagesByChatIdFromLocalDB,
} from "@/indexedDB/services/messages";

import { processAvatarBucketUrl } from "@/lib/utils";
import { queryClient } from "@/queryClient";
import { prepareMemberForExistingGroupChat } from "./members";
import { processMessageContent } from "./messages";
import { addPinnedMessagesToLocalDB } from "@/indexedDB/services/pinnedMessages";
import {
  DEFAULT_SYNC_DATE,
  CHAT_HUB_ID,
  MAX_ROTATION_MEMBER_COUNT,
  P2P_ROTATION_MESSAGE_INTERVAL,
} from "@/config/constants";
import { getUserData } from "@/server/services/userService";
import { GroupChatFormType, GroupChatFormSchema } from "@/schemas/chat";
import { useNewGroupChatState } from "@/zustand/NewGroupChatState";
import { useChatListState } from "@/zustand/ChatListState";
import { navigateToChat, navigateToChatList } from "@/lib/navigationRef";
import { isDeletedUserLabel } from "@/lib/deletedSender";
import { removeChatDataFromLocalDB } from "@/indexedDB/services/chatTeardown";
import { parseP2PPeer } from "@/lib/p2pPeer";
import {
  abortEpochRotation,
  commitEpochRotation,
  fetchEpochs,
  initEpochRotation,
  uploadEpochPayloads,
} from "@/server/services/epochService";
import { getMembers } from "@/server/services/memberService";
import { ApiError } from "@/server/apiError";
import { useDebugRotationState } from "@/zustand/DebugRotationState";
import {
  setCachedCSK,
  selfEncapsulateCSK,
  storeEpochPayloads,
} from "./epochKeyCache";

export const loadChats = async ({
  take,
  skip,
}: {
  take: number;
  skip: number;
}) => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  // const setPinnedMessageIdsForChat =
  //   usePinnedMessageState.getState().setPinnedMessageIdsForChat;

  const setFinishedLoadingFirstPageOfChatList =
    useChatListState.getState().setFinishedLoadingFirstPageOfChatList;

  try {
    // Get chat list from server
    const chatList = await getEnrichedChats({
      chats_take: take,
      chats_skip: skip,
    });

    const groupChats: ChatEntity[] = await Promise.all(
      chatList.enriched_chats.map(async (groupChat) => {
        // process and decrypt last message
        let lastMessageEntry: MessageEntity | null = null;

        if (groupChat.chat.last_message) {
          // Decrypt last message content. If decryption fails we still
          // persist a placeholder MessageEntity flagged as `failed` so the
          // chat-list preview shows "Decrypting…" and the retry orchestrator
          // can heal the denormalized `chats.lastMessage` once keys arrive.
          //
          // Empty `secure_payload` at epoch 0 is the transient "missing eCSK"
          // case: we skip the decrypt and don't bump `decryptionAttempts`. For
          // rotated epochs (>0) the CSK comes from the epoch cache, so we still
          // attempt even without an inline secure_payload.
          const lastEpoch = groupChat.chat.last_message.key_epoch ?? 0;
          const hasKey = !!groupChat.member.secure_payload || lastEpoch > 0;
          const decrypted = hasKey
            ? await tryDecrypt(() =>
                decryptMessageContent(
                  groupChat.chat.id,
                  lastEpoch,
                  groupChat.member.secure_payload,
                  groupChat.chat.last_message!.content,
                  groupChat.chat.last_message!.attachment_meta,
                ),
              )
            : ({ ok: false } as const);

          lastMessageEntry = {
            _clientId:
              groupChat.chat.last_message.client_id ||
              groupChat.chat.last_message.id, // Use client_id if present for optimistic updates, otherwise use server id
            id: groupChat.chat.last_message.id,
            chatId: groupChat.chat.id,
            sender: groupChat.chat.last_message.sender,
            ...(groupChat.chat.last_message.sender_deleted
              ? { senderDeleted: true }
              : {}),
            timestamp: extractTimestampFromUUID7(
              groupChat.chat.last_message.id,
            ),
            category: groupChat.chat.last_message.category,
            content: decrypted.ok ? decrypted.data : { text: "" },
            hasAttachment: !!groupChat.chat.last_message.attachment_meta,
            isEdited: groupChat.chat.last_message.is_edited,
            isOutgoing:
              userFingerPrint.username === groupChat.chat.last_message.sender,
            isReply: !!groupChat.chat.last_message.parent_id,
            // thread logic
            commentCount: groupChat.chat.last_message.comment_count,
            senderAvatar: groupChat.chat.last_message.sender_avatar,
            parentCommentCount:
              groupChat.chat.last_message.parent_comment_count,
            // comment thread
            threadId: groupChat.chat.last_message.thread_id,
            commentSentAt: null, // No longer needed with UUID7
            sendingStatus: MessageSendingStatus.SENT,
            decryptionStatus: decrypted.ok ? "ok" : "failed",
            // Only burn an attempt when we actually invoked a decryptor;
            // a missing eCSK is transient and shouldn't consume retry budget.
            ...(decrypted.ok || !hasKey ? {} : { decryptionAttempts: 1 }),
          };
        }

        // setPinnedMessageIdsForChat(
        //   groupChat.chat.id,
        //   groupChat.pinned_messages.map((m) => ({
        //     id: m.id,
        //     timestamp: m.sent_at,
        //   })),
        // );

        const pinnedMessageIds = groupChat.pinned_messages.map((m) => m.id);

        // pre save pinned messages for group chats in indexxed db
        const pinnedMessages = await processMessageContent(
          groupChat.chat.id,
          groupChat.member.secure_payload,
          groupChat.pinned_messages,
        );

        // TO DO => this is temporary.
        // TO DO => use livequery hook from dexie to get pinned messages when needed

        // add "isPinned" label before storin in indexed db
        const updatedPinnedMessages = pinnedMessages.map((msg) => ({
          ...msg,
          isPinned: true,
        }));

        // store pinned msg in IndexedDB - messages collection
        await addMessagesToLocalDB(updatedPinnedMessages);
        // store pinned msg in IndexedDB - pinnedMessages collection
        await addPinnedMessagesToLocalDB(pinnedMessages);

        return {
          id: groupChat.chat.id,
          eCSK: groupChat.member.secure_payload,
          currentEpoch: groupChat.chat.current_epoch ?? 0,
          hubId: groupChat.chat.hub_id,
          name: groupChat.chat.name,
          description: groupChat.chat.description,
          type: ChatType.GROUP,
          avatar: {
            attachmentId: groupChat.chat.picture,
          },
          timestamp: groupChat.chat.timestamp,
          lastSyncedAt: DEFAULT_SYNC_DATE,
          memberCount: groupChat.chat.member_count,
          lastMessage: lastMessageEntry,
          // TO DO => FIX THIS ON BE
          lastMessageTimestamp: groupChat.chat.last_message?.id
            ? extractTimestampFromUUID7(groupChat.chat.last_message.id)
            : undefined,
          unreadCount: groupChat.unread_count,
          isMuted: false,
          isPinned: groupChat.is_pinned,
          isArchived: groupChat.is_archived || false,
          isPublic: groupChat.chat.is_searchable,
          allowThreads: !!groupChat.chat.threads_enabled,
          canPost:
            groupChat.chat.can_post === null
              ? MemberRole.MEMBER
              : groupChat.chat.can_post,
          pinnedMessageIds,
          initStatus: InitChatStatus.SUCCESS,
          roleAsMember: {
            username: groupChat.member.username,
            role: groupChat.member.role,
          },
          // skipMsg: 0,
          firstPhaseMsgListInit: false,
          fullMsgListInit: false,
        };
      }),
    );

    // Process and format private chats
    const privateChats: ChatEntity[] = await Promise.all(
      chatList.enriched_p2p_chats.map(async (privateChat) => {
        // NEVER throw here — this runs inside one Promise.all, and user_two
        // is a plain username string on the Umbra backend (incl. the
        // "Deleted User" label). parseP2PPeer accepts both wire shapes.
        const parsed_user_two = parseP2PPeer(privateChat.p2p_chat.user_two) ?? {
          username: "",
          profileImage: null,
        };

        // process and decrypt last message
        let lastMessageEntry: MessageEntity | null = null;

        if (privateChat.p2p_chat.last_message) {
          const lastEpoch = privateChat.p2p_chat.last_message.key_epoch ?? 0;
          const hasKey = !!privateChat.p2p_chat.secure_payload || lastEpoch > 0;
          const decrypted = hasKey
            ? await tryDecrypt(() =>
                decryptMessageContent(
                  privateChat.p2p_chat.id,
                  lastEpoch,
                  privateChat.p2p_chat.secure_payload,
                  privateChat.p2p_chat.last_message!.content,
                  privateChat.p2p_chat.last_message!.attachment_meta,
                ),
              )
            : ({ ok: false } as const);

          lastMessageEntry = {
            _clientId:
              privateChat.p2p_chat.last_message.client_id ||
              privateChat.p2p_chat.last_message.id, // Use client_id if present for optimistic updates, otherwise use server id
            id: privateChat.p2p_chat.last_message.id,
            chatId: privateChat.p2p_chat.id,
            sender: privateChat.p2p_chat.last_message.sender,
            ...(privateChat.p2p_chat.last_message.sender_deleted
              ? { senderDeleted: true }
              : {}),
            timestamp: extractTimestampFromUUID7(
              privateChat.p2p_chat.last_message.id,
            ),
            category: privateChat.p2p_chat.last_message.category,
            content: decrypted.ok ? decrypted.data : { text: "" },
            hasAttachment: !!privateChat.p2p_chat.last_message.attachment_meta,
            isEdited: privateChat.p2p_chat.last_message.is_edited,
            isOutgoing:
              userFingerPrint.username ===
              privateChat.p2p_chat.last_message.sender,
            isReply: !!privateChat.p2p_chat.last_message.parent_id,
            // thread logic
            commentCount: privateChat.p2p_chat.last_message.comment_count,
            senderAvatar: privateChat.p2p_chat.last_message.sender_avatar,
            parentCommentCount:
              privateChat.p2p_chat.last_message.parent_comment_count,
            // comment thread
            threadId: privateChat.p2p_chat.last_message.thread_id,
            commentSentAt: null, // No longer needed with UUID7
            sendingStatus: MessageSendingStatus.SENT,
            decryptionStatus: decrypted.ok ? "ok" : "failed",
            ...(decrypted.ok || !hasKey ? {} : { decryptionAttempts: 1 }),
          };
        }

        // setPinnedMessageIdsForChat(
        //   privateChat.p2p_chat.id,
        //   privateChat.pinned_messages.map((m) => ({
        //     id: m.id,
        //     timestamp: m.sent_at,
        //   })),
        // );

        const pinnedMessageIds = privateChat.pinned_messages.map((m) => m.id);

        // pre save pinned messages for p2p chats in indexxed db
        const pinnedMessages = await processMessageContent(
          privateChat.p2p_chat.id,
          privateChat.p2p_chat.secure_payload,
          privateChat.pinned_messages,
        );

        // add "isPinned" label before storin in indexed db
        const updatedPinnedMessages = pinnedMessages.map((msg) => ({
          ...msg,
          isPinned: true,
        }));

        // store in IndexedDB
        await addMessagesToLocalDB(updatedPinnedMessages);

        // store pinned msg in IndexedDB - pinnedMessages collection
        await addPinnedMessagesToLocalDB(pinnedMessages);

        return {
          id: privateChat.p2p_chat.id,
          eCSK: privateChat.p2p_chat.secure_payload,
          currentEpoch: privateChat.p2p_chat.current_epoch ?? 0,
          hubId: null,
          name: parsed_user_two.username,
          type: ChatType.P2P,
          avatar: {
            attachmentId: parsed_user_two.profileImage ?? "",
            secondMemberId: parsed_user_two.username,
          },
          timestamp: new Date(privateChat.p2p_chat.timestamp).getTime(),
          lastSyncedAt: DEFAULT_SYNC_DATE,
          memberCount: 2,
          lastMessage: lastMessageEntry,
          // TO DO => FIX THIS ON BE
          lastMessageTimestamp: privateChat.p2p_chat.last_message?.id
            ? extractTimestampFromUUID7(privateChat.p2p_chat.last_message.id)
            : undefined,
          unreadCount: privateChat.unread_count,
          isMuted: false,
          isPinned: privateChat.is_pinned,
          isArchived: privateChat.is_archived || false,
          isPublic: false,
          allowThreads: false,
          canPost: MemberRole.MEMBER,
          pinnedMessageIds,
          initStatus: InitChatStatus.SUCCESS,
          roleAsMember: {
            username: userFingerPrint.username,
            role:
              userFingerPrint.username === parsed_user_two.username
                ? MemberRole.MEMBER
                : MemberRole.OWNER,
          },
          // skipMsg: 0,
          firstPhaseMsgListInit: false,
          fullMsgListInit: false,
        };
      }),
    );

    const mutedChatIds = await getMutedChats(userFingerPrint.username);

    const allChats = [...groupChats, ...privateChats].map((chat) => ({
      ...chat,
      isMuted: mutedChatIds.includes(chat.id),
    }));

    // Bulk add chat list
    await addChatsToLocalDB(allChats);

    const chatIds = allChats.map((chat) => chat.id);

    setFinishedLoadingFirstPageOfChatList(true);

    return chatIds;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    setFinishedLoadingFirstPageOfChatList(true);
    return [];
  }
};

export const muteChat = async (chatId: string) => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }
  if (!chatId) {
    throw new Error("Invalid chat ID provided.");
  }

  const acttiveChatId = useNavigationState.getState().activeChatId;
  const setActiveChatMuted = useNavigationState.getState().setActiveChatMuted;
  try {
    // optimistic update on active chat
    if (acttiveChatId === chatId) {
      setActiveChatMuted(true);
    }
    // optimistic update on local db
    await updateChatInLocalDB(chatId, { isMuted: true });

    // call server to mute chat
    await muteChatById(chatId);
  } catch (error) {
    // Handle revert of optimistic updates
    setActiveChatMuted(false);
    await updateChatInLocalDB(chatId, { isMuted: false });

    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error("Cannot mute chat.");
  }
};

export const unmuteChat = async (chatId: string) => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }
  if (!chatId) {
    throw new Error("Invalid chat ID provided.");
  }

  const acttiveChatId = useNavigationState.getState().activeChatId;
  const setActiveChatMuted = useNavigationState.getState().setActiveChatMuted;
  try {
    // optimistic update on active chat
    if (acttiveChatId === chatId) {
      setActiveChatMuted(false);
    }
    // optimistic update on local db
    await updateChatInLocalDB(chatId, { isMuted: false });

    // call server to unmute chat
    await unmuteChatById(chatId);
  } catch (error) {
    // Handle revert of optimistic updates
    setActiveChatMuted(true);
    await updateChatInLocalDB(chatId, { isMuted: true });

    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error("Cannot create unmute chat.");
  }
};

export const pinChat = async (chatId: string) => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }
  if (!chatId) {
    throw new Error("Invalid chat ID provided.");
  }
  try {
    // optimistic update on local db
    await updateChatInLocalDB(chatId, { isPinned: true });
    // call server to pin chat
    await pinChatById(chatId, true);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    // Handle revert of optimistic updates
    await updateChatInLocalDB(chatId, { isPinned: false });
    // if (error instanceof Error && error.message) throw error;
    throw new Error("Cannot pin chat.");
  }
};

export const unpinChat = async (chatId: string) => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }
  if (!chatId) {
    throw new Error("Invalid chat ID provided.");
  }
  try {
    // optimistic update on local db
    await updateChatInLocalDB(chatId, { isPinned: false });
    // call server to unpin chat
    await pinChatById(chatId, false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    // Handle revert of optimistic updates
    await updateChatInLocalDB(chatId, { isPinned: true });
    // if (error instanceof Error && error.message) throw error;
    throw new Error("Cannot unpin chat.");
  }
};

export const deleteP2PChat = async (chatId: string) => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }
  if (!chatId) {
    throw new Error("Invalid chat ID provided.");
  }
  try {
    // get chat from local db
    const chatDetails = await getChatFromLocalDB(chatId);
    if (!chatDetails) {
      return;
    }

    // call server to delete chat
    await deleteP2PChatById(chatId);

    // if chat id is active, navigate away via URL (URL-first pattern)
    const activeChatId = useNavigationState.getState().activeChatId;
    if (chatId === activeChatId) {
      navigateToChatList();
    }

    // delete messages + attachments (incl. OPFS blobs) + links from local db
    await removeChatDataFromLocalDB(chatId);
    // remove chat from local db
    await removeChatFromLocalDB(chatId);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    // Handle revert of optimistic updates
    // await updateChatInLocalDB(chatId, { isPinned: false });
    // if (error instanceof Error && error.message) throw error;
    throw new Error("Cannot delete chat.");
  }
};

export const deleteGroupChat = async (chatId: string) => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }
  if (!chatId) {
    throw new Error("Invalid chat ID provided.");
  }
  try {
    // get chat from local db
    const chatDetails = await getChatFromLocalDB(chatId);
    if (!chatDetails) {
      return;
    }

    // check if group chat and user role is owner
    if (
      chatDetails.type === ChatType.GROUP &&
      chatDetails.roleAsMember.role !== MemberRole.OWNER
    ) {
      throw new Error("Not enough permission.");
    }

    // call server to delete chat
    await deleteGroupChatById(chatId);

    // if chat id is active, navigate away via URL (URL-first pattern)
    const activeChatId = useNavigationState.getState().activeChatId;
    if (chatId === activeChatId) {
      navigateToChatList();
    }

    // delete messages + attachments (incl. OPFS blobs) + links from local db
    await removeChatDataFromLocalDB(chatId);
    // remove chat from local db
    await removeChatFromLocalDB(chatId);
    // invalidate the query for the current user's public chats
    queryClient.invalidateQueries({
      queryKey: ["admin-user-public-chat"],
    });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    // Handle revert of optimistic updates
    // await updateChatInLocalDB(chatId, { isPinned: false });
    // if (error instanceof Error && error.message) throw error;
    throw new Error("Cannot delete chat.");
  }
};

export const archiveChat = async (chatId: string) => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }
  if (!chatId) {
    throw new Error("Invalid chat ID provided.");
  }
  try {
    // optimistic update on local db
    await updateChatInLocalDB(chatId, { isArchived: true });
    // call server to archive chat
    await archiveChatById(chatId, true);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    // Handle revert of optimistic updates
    await updateChatInLocalDB(chatId, { isArchived: false });
    // if (error instanceof Error && error.message) throw error;
    throw new Error("Cannot archive chat.");
  }
};

export const unarchiveChat = async (chatId: string) => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }
  if (!chatId) {
    throw new Error("Invalid chat ID provided.");
  }
  try {
    // optimistic update on local db
    await updateChatInLocalDB(chatId, { isArchived: false });
    // call server to unarchive chat
    await archiveChatById(chatId, false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    // Handle revert of optimistic updates
    await updateChatInLocalDB(chatId, { isArchived: true });
    // if (error instanceof Error && error.message) throw error;
    throw new Error("Cannot unarchive chat.");
  }
};

export const createDMChat = async (receiverUsername: string) => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }
  if (!receiverUsername) {
    throw new Error("Receiver username not provided.");
  }
  // The deleted-user label is not an account — and worse, the name-based
  // existing-chat lookup below would silently open an ARBITRARY chat already
  // renamed "Deleted User". Refuse outright.
  if (isDeletedUserLabel(receiverUsername)) {
    throw new Error("Cannot start a chat with a deleted account.");
  }

  // Global state references
  const setActiveChatInitStatus =
    useNavigationState.getState().setActiveChatInitStatus;

  // Generate new chat Id
  const newChatId = generateUuid7();

  // Set only AFTER the optimistic local write. The catch below must not
  // revert/navigate for PRE-write failures (e.g. getUserData 404 for a
  // deleted or nonexistent peer) — doing so ejected the user from whatever
  // chat they were reading, with nothing to revert.
  let optimisticChatWritten = false;

  try {
    const existingChat = await getChatByNameFromLocalDB(receiverUsername);

    // If chat already exists, navigate to chat id via URL (URL-first pattern)
    if (existingChat) {
      navigateToChat(existingChat.id, { replace: true });
      return existingChat.id;
    }

    // If chat does NOT exist, process new chat
    if (!existingChat) {
      // Generate CSK
      const chatSecretKey = generateChatSecretKey();

      // Get current user's private key
      const senderPrivateKeys = await decryptPrivateKey(
        userFingerPrint.ePrK,
        userFingerPrint.dk,
      );

      // Generate secure payload for owner
      const senderSecurePayload = encapsulateSecurePayload({
        receiverPublicKeys: userFingerPrint.ePuK,
        senderPrivateKeys,
        chatSecretKey,
      });

      // Get user two encrypted data
      const receiverUserData = await getUserData(receiverUsername);

      // Generate secure payload for receiver
      const receiverSecurePayload = encapsulateSecurePayload({
        receiverPublicKeys: receiverUserData.public_keys,
        senderPrivateKeys,
        chatSecretKey,
      });

      const securePayload = JSON.stringify({
        [userFingerPrint.username]: senderSecurePayload,
        [receiverUserData.username]: receiverSecurePayload,
      });

      // OPTIMISTIC STUFF
      // add new chat to local db
      await addChatToLocalDB({
        id: newChatId,
        eCSK: senderSecurePayload,
        currentEpoch: 0,
        hubId: null,
        name: receiverUsername,
        type: ChatType.P2P,
        avatar: {
          attachmentId: receiverUserData.profile_image,
          secondMemberId: receiverUsername,
        },
        timestamp: new Date().getTime(),
        lastSyncedAt: DEFAULT_SYNC_DATE,
        memberCount: 2,
        unreadCount: 0,
        isMuted: false,
        isPinned: false,
        isArchived: false,
        isPublic: false,
        allowThreads: false,
        canPost: MemberRole.MEMBER,
        // pinnedMessageIds: [],
        initStatus: InitChatStatus.PENDING,
        roleAsMember: {
          username: userFingerPrint.username,
          role: MemberRole.OWNER,
        },
        lastMessage: null,
        firstPhaseMsgListInit: false,
        fullMsgListInit: false,
      });
      optimisticChatWritten = true;

      // navigate to new chat via URL (URL-first pattern)
      navigateToChat(newChatId);

      await createP2PChat({
        id: newChatId,
        user_one: userFingerPrint.username,
        user_two: receiverUserData.username,
        secure_payload: securePayload,
      });

      // update chat init status
      await updateChatInLocalDB(newChatId, {
        initStatus: InitChatStatus.SUCCESS,
      });

      // Check using newChatId directly — Zustand may not have synced yet
      // since navigateToChat is URL-first and state updates asynchronously
      if (newChatId === useNavigationState.getState().activeChatId) {
        setActiveChatInitStatus(InitChatStatus.SUCCESS);
      }
    }
  } catch (error) {
    // Revert the optimistic updates — but ONLY if they happened. A pre-write
    // failure has nothing to remove and must not navigate the user away from
    // the chat they were viewing.
    if (optimisticChatWritten) {
      await removeChatFromLocalDB(newChatId);
      // navigate back from active chat via URL (URL-first pattern)
      navigateToChatList();
    }
    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error("Cannot create private chat.");
  }
};

export const createNewGroupChat = async (payload: GroupChatFormType) => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }
  // Validate the payload with the GroupChatFormSchema (zod)
  const isValidPayload = GroupChatFormSchema.safeParse(payload);

  // If the payload is invalid, throw an error with the first error message
  if (!isValidPayload.success) {
    throw new Error(isValidPayload.error.errors[0].message);
  }

  const { newChatId, members } = useNewGroupChatState.getState();

  if (!newChatId) {
    throw new Error("Chat ID not provided.");
  }

  if (!members) {
    throw new Error("Members not provided.");
  }

  // extra validation -> check if current user is in members
  const isCurrentUserMember = members.find(
    (member) => member.username === userFingerPrint.username,
  );

  if (!isCurrentUserMember) {
    throw new Error("Auth user not found in members.");
  }

  // Global state references
  const setActiveChatInitStatus =
    useNavigationState.getState().setActiveChatInitStatus;
  const resetNewGroupChatState =
    useNewGroupChatState.getState().resetNewGroupChatState;

  try {
    // build new group chat payload
    const newChatPayload: CreateGroupChatPayload = {
      chat: {
        id: newChatId,
        hub_id: null,
        name: payload.title,
        picture: payload.avatar,
        is_private_p2p: false,
        member_count: members.length,
        timestamp: new Date().getTime(),
        last_message: null,
        description: payload.description,
        is_searchable: payload.isSearchable,
        threads_enabled: payload.allowThreads,
        can_post: payload.canPost,
      },
      members: members,
    };

    // set owner secure payload => used for immediate msg sending after chat create
    // used for encrypting msg content
    const ownerSecurePayload =
      useNewGroupChatState.getState().ownerSecurePayload;
    if (!ownerSecurePayload) {
      throw new Error("Owner secure payload not provided.");
    }

    // OPTIMISTIC STUFF
    // add chat entry to local db
    await addChatToLocalDB({
      id: newChatId,
      hubId: null,
      eCSK: ownerSecurePayload,
      currentEpoch: 0,
      name: payload.title,
      description: payload.description,
      type: ChatType.GROUP,
      avatar: {
        attachmentId: payload.avatar,
      },
      timestamp: new Date().getTime(),
      lastSyncedAt: DEFAULT_SYNC_DATE,
      memberCount: members.length,
      unreadCount: 0,
      isMuted: false,
      isPinned: false,
      isArchived: false,
      isPublic: payload.isSearchable,
      allowThreads: !!payload.allowThreads,
      canPost: payload.canPost === null ? MemberRole.MEMBER : payload.canPost,
      initStatus: InitChatStatus.PENDING,
      // pinnedMessageIds: [],
      roleAsMember: {
        username: userFingerPrint.username,
        role: MemberRole.OWNER,
      },
      lastMessage: null,
      firstPhaseMsgListInit: false,
      fullMsgListInit: false,
    });

    // navigate to new chat via URL (URL-first pattern)
    navigateToChat(newChatId);

    await createGroupChat(newChatPayload);

    // update chat init status
    await updateChatInLocalDB(newChatId, {
      initStatus: InitChatStatus.SUCCESS,
    });

    // Check using newChatId directly — Zustand may not have synced yet
    // since navigateToChat is URL-first and state updates asynchronously
    if (newChatId === useNavigationState.getState().activeChatId) {
      setActiveChatInitStatus(InitChatStatus.SUCCESS);
    }
  } catch (error) {
    // Handle revert of optimistic updates
    // remove chat entry from local db
    removeChatFromLocalDB(newChatId);
    // navigate back from active chat via URL (URL-first pattern)
    navigateToChatList();

    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error("Cannot create group chat.");
  } finally {
    // reset new group chat store
    resetNewGroupChatState();
  }
};

export const createNewAIChat = async (payload: GroupChatFormType) => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }
  // Validate the payload with the GroupChatFormSchema (zod)
  const isValidPayload = GroupChatFormSchema.safeParse(payload);

  // If the payload is invalid, throw an error with the first error message
  if (!isValidPayload.success) {
    throw new Error(isValidPayload.error.errors[0].message);
  }

  const { newChatId, members } = useNewGroupChatState.getState();

  if (!newChatId) {
    throw new Error("Chat ID not provided.");
  }

  if (!members) {
    throw new Error("Members not provided.");
  }

  // extra validation -> check if current user is in members
  const isCurrentUserMember = members.find(
    (member) => member.username === userFingerPrint.username,
  );

  if (!isCurrentUserMember) {
    throw new Error("Auth user not found in members.");
  }

  // Global state references
  const setActiveChatInitStatus =
    useNavigationState.getState().setActiveChatInitStatus;
  const resetNewGroupChatState =
    useNewGroupChatState.getState().resetNewGroupChatState;

  try {
    // set owner secure payload => used for immediate msg sending after chat create
    // used for encrypting msg content
    const ownerSecurePayload =
      useNewGroupChatState.getState().ownerSecurePayload;
    if (!ownerSecurePayload) {
      throw new Error("Owner secure payload not provided.");
    }

    // OPTIMISTIC STUFF
    // add chat entry to local db
    await addChatToLocalDB({
      id: newChatId,
      hubId: CHAT_HUB_ID,
      eCSK: ownerSecurePayload,
      currentEpoch: 0,
      name: payload.title,
      description: payload.description,
      type: ChatType.GROUP,
      avatar: {
        attachmentId: payload.avatar,
      },
      timestamp: new Date().getTime(),
      lastSyncedAt: DEFAULT_SYNC_DATE,
      memberCount: members.length,
      unreadCount: 0,
      isMuted: false,
      isPinned: false,
      isArchived: false,
      isPublic: payload.isSearchable,
      allowThreads: !!payload.allowThreads,
      canPost: payload.canPost === null ? MemberRole.MEMBER : payload.canPost,
      initStatus: InitChatStatus.PENDING,
      // pinnedMessageIds: [],
      roleAsMember: {
        username: userFingerPrint.username,
        role: MemberRole.OWNER,
      },
      lastMessage: null,
      lastMessageTimestamp: new Date().toISOString(),
      firstPhaseMsgListInit: false,
      fullMsgListInit: false,
    });

    // navigate to new chat via URL (URL-first pattern)
    navigateToChat(newChatId);

    // TO DO  => ADD server label "groupingId"
    // ________
    // build new group chat payload
    const newChatPayload: CreateGroupChatPayload = {
      chat: {
        id: newChatId,
        hub_id: CHAT_HUB_ID,
        name: payload.title,
        picture: payload.avatar,
        is_private_p2p: false,
        member_count: members.length,
        timestamp: new Date().getTime(),
        last_message: null,
        description: payload.description,
        is_searchable: payload.isSearchable,
        threads_enabled: payload.allowThreads,
        can_post: payload.canPost,
      },
      members: members,
    };

    const chatId = await createGroupChat(newChatPayload);

    // update chat init status
    await updateChatInLocalDB(newChatId, {
      initStatus: InitChatStatus.SUCCESS,
    });

    // Check using newChatId directly — Zustand may not have synced yet
    // since navigateToChat is URL-first and state updates asynchronously
    if (newChatId === useNavigationState.getState().activeChatId) {
      setActiveChatInitStatus(InitChatStatus.SUCCESS);
    }
    return chatId;
  } catch (error) {
    // Handle revert of optimistic updates
    // remove chat entry from local db
    removeChatFromLocalDB(newChatId);
    // navigate back from active chat via URL (URL-first pattern)
    navigateToChatList();

    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error("Cannot create group chat.");
  } finally {
    // reset new group chat store
    resetNewGroupChatState();
  }
};

export const updateGroupChat = async (
  chatId: string,
  values: GroupChatFormType,
) => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }
  if (!chatId) {
    throw new Error("Invalid chat ID provided.");
  }

  const acttiveChatId = useNavigationState.getState().activeChatId;
  const setActiveChatName = useNavigationState.getState().setActiveChatName;
  const setActiveChatIsPublic =
    useNavigationState.getState().setActiveChatIsPublic;
  const setActiveChatDescription =
    useNavigationState.getState().setActiveChatDescription;
  const setActiveChatAvatar = useNavigationState.getState().setActiveChatAvatar;

  try {
    // TO DO => cherck for any optimistic updates if necessary
    // // optimistic update on active chat
    // if (acttiveChatId === chatId) setActiveChatMuted(true);
    // // optimistic update on local db
    // await updateChatInLocalDB(chatId, { isMuted: true });

    // TO DO => we need to properly implement PUT/PATCH on BE
    // get chat details to send it back with updated values
    const chatDetails = await getGroupChatById(chatId);
    // new chat details
    const newChatDetails: GroupChatResponseType = {
      ...chatDetails,
      name: values.title,
      description: values.description,
      is_searchable: values.isSearchable,
      threads_enabled: values.allowThreads,
      can_post: values.canPost,
      picture:
        values.avatar !== undefined ? values.avatar : chatDetails.picture,
    };

    // call server to update chat
    await patchGroupChat(newChatDetails);

    // TO DO => check if this is necessary since we already will do same updates on ws events
    if (acttiveChatId === chatId) {
      setActiveChatName(values.title);
      setActiveChatDescription(values.description);
      setActiveChatIsPublic(values.isSearchable);
      setActiveChatAvatar({
        attachmentId: values.avatar,
      });
    }

    // update on local db
    await updateChatInLocalDB(chatId, {
      name: values.title,
      description: values.description,
      isPublic: values.isSearchable,
      avatar: {
        attachmentId: values.avatar,
      },
    });
  } catch (error) {
    // TO DO => cherck ffor any  revert of optimistic updates

    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error("Cannot update chat.");
  }
};

export const searchPublicGroupChats = async (
  searchTerm: string,
  offset: number,
  count: number,
): Promise<SearchableGroupChatResponse[]> => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  try {
    const response = await getPublicGroupChats({
      prefix: searchTerm,
      accessor: userFingerPrint.username,
      offset,
      count,
    });

    // Process the results (avatar processing, mark auth user, etc.)
    const processedResults = response.map((result) => {
      const avatarUrl = processAvatarBucketUrl(result.picture || "");
      return {
        ...result,
        picture: avatarUrl,
      };
    });

    return processedResults;
  } catch (error) {
    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error("Cannot fetch public group chats.");
  }
};

export const joinPublicGroupChat = async (chatId: string) => {
  if (!chatId) {
    throw new Error("Invalid chat ID provided.");
  }
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    return;
  }

  try {
    await sendPublicGroupJoinRequest({
      chat_id: chatId,
      public_keys: userFingerPrint.ePuK,
    });

    // invalidate the public groups for curent user requests cache
    // TO DO => check if this is necessary since we invalidate on ws events as well
    queryClient.invalidateQueries({
      queryKey: ["public-groups-user-requests"],
    });
  } catch (error) {
    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error("Cannot join public group chat.");
  }
};

export const denyJoinRequest = async (chatId: string, username: string) => {
  if (!chatId || !username) {
    throw new Error("Invalid chat ID or username provided.");
  }
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    return;
  }

  try {
    await denyPublicGroupJoinRequest(chatId, username);
    // Invalidate this public group users requests cache
    // TO DO => check if this is necessary since we invalidate on ws events as well
    queryClient.invalidateQueries({
      queryKey: ["public-group-users-requests", chatId],
    });
  } catch (error) {
    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error("Cannot deny join request.");
  }
};

export const acceptJoinRequest = async (chatId: string, username: string) => {
  if (!chatId || !username) {
    throw new Error("Invalid chat ID or username provided.");
  }
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    return;
  }

  try {
    const chatDetails = await getChatFromLocalDB(chatId);
    if (!chatDetails) {
      throw new Error("Chat not found.");
    }

    // check if chat is public and current user is owner or admin
    if (
      chatDetails.isPublic &&
      ![MemberRole.OWNER, MemberRole.ADMIN].includes(
        chatDetails.roleAsMember.role,
      )
    ) {
      throw new Error("Not enough permission.");
    }

    const newMember = await prepareMemberForExistingGroupChat(
      username,
      chatId,
      chatDetails.eCSK,
    );

    if (!newMember) {
      throw new Error("New member data is not valid.");
    }

    await acceptPublicGroupJoinRequest(newMember);

    await queryClient.invalidateQueries({
      queryKey: ["public-group-users-requests", chatId],
    });
  } catch (error) {
    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error("Cannot accept join request.");
  }
};

// ---------------------------------------------------------------------------
// CSK Rotation — Phase 2
// ---------------------------------------------------------------------------

/**
 * Rotates the Chat Secret Key (CSK) for a group chat.
 *
 * Steps (per spec §7.1):
 *  1. Verify auth
 *  2. Fetch chat details from local DB (current epoch, eCSK)
 *  3. Decrypt own private key
 *  4. Generate a fresh CSK
 *  5. Fetch all current members & their public keys
 *  6. Encapsulate the new CSK for each member
 *  7. POST rotation to server
 *  8. Update local chat state (epoch, eCSK) + cache new CSK
 *
 * @param chatId  — target chat
 * @param reason  — human-readable reason (e.g. "member_removed")
 * @throws on auth failure, missing chat, or server rejection (409 = epoch conflict)
 */
const MAX_ROTATION_RETRIES = 2;
// Each encapsulated payload is ~2.4 KB hex; with JSON overhead, ~2.5 KB per
// entry. 6,000 entries ≈ 15 MB per chunk — comfortably under the nginx 30 MB
// body limit while minimising round-trips for typical group sizes.
const PAYLOAD_CHUNK_SIZE = 6000;

const isApiConflict = (error: unknown): boolean => {
  if (!(error instanceof ApiError)) {
    return false;
  }
  // The chat_gateway maps gRPC errors to HTTP 408 (handle_error in
  // chat_gateway/src/common.rs), so we can't rely on status alone to detect
  // a rotation conflict. Match by status OR by validation message.
  return (
    error.status === 409 ||
    /current_epoch is \d+/.test(error.message) ||
    /Concurrent rotation in progress/.test(error.message) ||
    /Pending rotation marker/.test(error.message) ||
    /Rotation not found/.test(error.message) ||
    /new_epoch must be \d+/.test(error.message) ||
    /Missing encapsulated key for member/.test(error.message) ||
    /Payload provided for non-member/.test(error.message)
  );
};

export const rotateCSK = async (
  chatId: string,
  reason?: string,
  _retryCount = 0,
): Promise<void> => {
  // Debug timing — instrumented in dev only; nil overhead in prod.
  const debug = import.meta.env.DEV;
  const t0 = debug ? performance.now() : 0;

  // 1. Auth check
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  // 2. Chat details — refetch from local DB on every attempt so we have
  //    the latest epoch after a retry.
  const chatDetails = await getChatFromLocalDB(chatId);
  if (!chatDetails) {
    throw new Error(`Chat ${chatId} not found in local DB`);
  }

  const currentEpoch = chatDetails.currentEpoch ?? 0;
  const isP2P = chatDetails.type === ChatType.P2P;

  // Group-size gate: CSK rotation is O(members) work, so it is limited to groups
  // at or below MAX_ROTATION_MEMBER_COUNT. Larger groups keep the static epoch-0
  // CSK (pre-rotation behavior) and never rotate. Every rotation trigger (member
  // removal, server-requested, manual) funnels through here, so this one check
  // covers them all.
  if ((chatDetails.memberCount ?? 0) > MAX_ROTATION_MEMBER_COUNT) {
    if (debug) {
      console.info(
        `[rotateCSK] skipped: chat ${chatId} has ${chatDetails.memberCount} members ` +
          `(> ${MAX_ROTATION_MEMBER_COUNT}); staying on epoch ${currentEpoch}`,
      );
    }
    return;
  }

  const refreshAndRetry = async (): Promise<void> => {
    if (isP2P) {
      // There is no single-P2P-chat read endpoint. The authoritative current
      // epoch is the highest committed epoch for the chat, so refresh from the
      // epoch records before retrying.
      const epochs = await fetchEpochs(chatId);
      if (epochs.length > 0) {
        const latest = Math.max(...epochs.map((e) => e.epoch));
        await updateChatInLocalDB(chatId, { currentEpoch: latest });
      }
    } else {
      const serverChat = await getGroupChatById(chatId);
      if (serverChat) {
        await updateChatInLocalDB(chatId, {
          currentEpoch: serverChat.current_epoch ?? 0,
        });
      }
    }
    return rotateCSK(chatId, reason, _retryCount + 1);
  };

  // 3. Init the rotation up-front. If another client is mid-rotation or our
  //    view of current_epoch is stale, the server returns 409 here and we
  //    haven't yet done any encapsulation work.
  let rotationId: string;
  let newEpoch: number;
  try {
    const initResp = await initEpochRotation(chatId, currentEpoch);
    rotationId = initResp.rotation_id;
    newEpoch = initResp.new_epoch;
  } catch (error) {
    if (isApiConflict(error) && _retryCount < MAX_ROTATION_RETRIES) {
      return refreshAndRetry();
    }
    throw error;
  }

  // From this point, any error must abort the rotation server-side so the
  // pending marker is released and we don't strand pending payload rows.
  try {
    // 4. Own private key
    const tDecryptStart = debug ? performance.now() : 0;
    const senderPrivateKeys = await decryptPrivateKey(
      userFingerPrint.ePrK,
      userFingerPrint.dk,
    );
    const tDecryptEnd = debug ? performance.now() : 0;

    // 5. Generate new CSK
    const tGenStart = debug ? performance.now() : 0;
    const newCSK = generateChatSecretKey();
    const tGenEnd = debug ? performance.now() : 0;

    // 6. Determine the recipients that need a new encapsulated CSK.
    //    Group chats enumerate members via the public /members endpoint, which
    //    paginates (default take=20), so paginate here to omit no member.
    //    P2P chats have exactly the two participants (self + peer); there is no
    //    members table, so build that list directly and skip the fetch.
    const members: { username: string; public_keys: string }[] = [];
    if (isP2P) {
      members.push({
        username: userFingerPrint.username,
        public_keys: userFingerPrint.ePuK,
      });
      // For P2P, the peer's username is stored as the chat's second-member id.
      const peerUsername =
        chatDetails.avatar?.secondMemberId ?? chatDetails.name;
      if (peerUsername && peerUsername !== userFingerPrint.username) {
        // Leave public_keys empty so the per-recipient fallback below fetches
        // the peer's current keys via getUserData — always fresh, and reuses
        // the same code path groups rely on for un-backfilled members.
        members.push({ username: peerUsername, public_keys: "" });
      }
    } else {
      const PAGE = 100;
      for (let skip = 0; ; skip += PAGE) {
        const page = await getMembers({ chat_id: chatId, skip, take: PAGE });
        members.push(...page);
        if (page.length < PAGE) {
          break;
        }
      }
    }

    // 7. Encapsulate per-member payloads, accumulating into chunks. Each
    //    chunk is uploaded as soon as it's full so memory peak stays bounded
    //    at ~PAYLOAD_CHUNK_SIZE entries even for very large groups.
    const tEncStart = debug ? performance.now() : 0;
    const tPostStart = debug ? performance.now() : 0;
    const memberPayloads: Record<string, string> = {};
    let chunk: Record<string, string> = {};
    let chunkCount = 0;
    let encapsulateAccum = 0;
    let postAccum = 0;

    const flushChunk = async (): Promise<void> => {
      if (chunkCount === 0) {
        return;
      }
      const tChunkStart = debug ? performance.now() : 0;
      await uploadEpochPayloads(chatId, rotationId, chunk);
      if (debug) {
        postAccum += performance.now() - tChunkStart;
      }
      chunk = {};
      chunkCount = 0;
    };

    for (const member of members) {
      if (!member.public_keys) {
        // Pre-5.2 member documents may not be backfilled yet; fall back to
        // the user record so rotation still works during migration.
        const userData = await getUserData(member.username);
        if (!userData?.public_keys) {
          throw new Error(
            `Cannot rotate CSK: public keys unavailable for ${member.username}`,
          );
        }
        member.public_keys = userData.public_keys;
      }

      const tEachStart = debug ? performance.now() : 0;
      const payload = encapsulateSecurePayload({
        receiverPublicKeys: member.public_keys,
        senderPrivateKeys,
        chatSecretKey: newCSK,
      });
      if (debug) {
        encapsulateAccum += performance.now() - tEachStart;
      }

      memberPayloads[member.username] = payload;
      chunk[member.username] = payload;
      chunkCount += 1;

      if (chunkCount >= PAYLOAD_CHUNK_SIZE) {
        await flushChunk();
      }
    }

    await flushChunk();
    const tEncEnd = debug ? performance.now() : 0;

    // 8. Commit. Server validates that every current member has a payload,
    //    advances current_epoch, and broadcasts key_rotated.
    await commitEpochRotation(chatId, rotationId);
    const tPostEnd = debug ? performance.now() : 0;

    // 9. Update local state
    const ownECSK = memberPayloads[userFingerPrint.username];

    await updateChatInLocalDB(chatId, {
      currentEpoch: newEpoch,
      eCSK: ownECSK,
    });

    setCachedCSK(chatId, newEpoch, newCSK);

    const tSelfStart = debug ? performance.now() : 0;
    const selfPayload = await selfEncapsulateCSK(newCSK);
    const tSelfEnd = debug ? performance.now() : 0;
    await storeEpochPayloads(chatId, [
      { epoch: newEpoch, selfECSK: selfPayload },
    ]);

    if (debug) {
      useDebugRotationState.getState().recordRotation(chatId, {
        members: members.length,
        decryptPrivateKey: tDecryptEnd - tDecryptStart,
        generateCSK: tGenEnd - tGenStart,
        encapsulateAll: encapsulateAccum,
        serverPost: postAccum + (tPostEnd - tEncEnd),
        selfEncapsulate: tSelfEnd - tSelfStart,
        total: performance.now() - t0,
        at: Date.now(),
      });
    }
    void tEncStart;
    void tPostStart;
  } catch (error) {
    // Best-effort: release the server-side rotation slot so subsequent
    // rotations don't have to wait for the TTL sweep.
    try {
      await abortEpochRotation(chatId, rotationId);
    } catch {
      // Swallow — server will reap on TTL.
    }

    if (isApiConflict(error) && _retryCount < MAX_ROTATION_RETRIES) {
      return refreshAndRetry();
    }
    throw error;
  }
};

// Chats with a forward-secrecy rotation check in flight. A burst of sends must
// not stack concurrent rotations for the same chat, so we gate on this set.
const p2pRotationInFlight = new Set<string>();

/**
 * P2P forward secrecy trigger. Groups rotate the CSK on membership change; P2P
 * chats have no membership events, so instead they rotate by message volume:
 * once P2P_ROTATION_MESSAGE_INTERVAL messages have accumulated at the current
 * epoch, rotate to a fresh CSK.
 *
 * Called fire-and-forget after a successful P2P send — it must never block or
 * fail the send, so all errors are swallowed (best-effort; the next send retries
 * the check). Concurrent rotation between the two peers is resolved server-side
 * by the epoch CAS: the loser gets a 409 and rotateCSK's own retry reconciles.
 */
export const maybeRotateP2PForForwardSecrecy = async (
  chatId: string,
): Promise<void> => {
  if (p2pRotationInFlight.has(chatId)) {
    return;
  }

  const chatDetails = await getChatFromLocalDB(chatId);
  if (!chatDetails || chatDetails.type !== ChatType.P2P) {
    return;
  }

  const currentEpoch = chatDetails.currentEpoch ?? 0;
  const count = await countMessagesAtEpochInLocalDB(chatId, currentEpoch);
  if (count < P2P_ROTATION_MESSAGE_INTERVAL) {
    return;
  }

  p2pRotationInFlight.add(chatId);
  try {
    await rotateCSK(chatId, "p2p_message_count");
  } catch (error) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn(`[maybeRotateP2P] rotation failed for ${chatId}`, error);
    }
  } finally {
    p2pRotationInFlight.delete(chatId);
  }
};
